import { z } from "zod";

const DimensionsVariableScopeSchema = z.enum([
  "CORNER_RADIUS",
  "WIDTH_HEIGHT",
  "GAP",
  "STROKE_FLOAT",
  "EFFECT_FLOAT",
  "OPACITY",
  "FONT_SIZE",
  "LINE_HEIGHT",
  "LETTER_SPACING",
  "PARAGRAPH_SPACING",
  "PARAGRAPH_INDENT",
  "FONT_WEIGHT",
]);

const StringVariableScopeSchema = z.enum(["TEXT_CONTENT", "FONT_FAMILY", "FONT_STYLE"]);

export type DimensionsVariableScopeType = z.infer<typeof DimensionsVariableScopeSchema>;
export const DimensionsVariableScopeType = DimensionsVariableScopeSchema.enum;

export type StringVariableScopeType = z.infer<typeof StringVariableScopeSchema>;
export const StringVariableScopeType = StringVariableScopeSchema.enum;

const VariableScope = z.string();

const ReferencedVariableOrigin = z.object({
  name: z.string(),
  remote: z.boolean(),
});

export const RGB = z.object({
  r: z.number(),
  g: z.number(),
  b: z.number(),
});

export const RGBA = RGB.extend({
  a: z.number(),
});

export const VariableAlias = z.object({
  type: z.literal("VARIABLE_ALIAS"),
  id: z.string(),
  resolvedValue: z.boolean().or(z.number()).or(z.string()).or(RGBA).or(RGB).optional(),
  referenceOrigin: ReferencedVariableOrigin.optional().or(z.null()),
});

export const VariableValue = z.boolean().or(z.number()).or(z.string()).or(RGBA).or(RGB).or(VariableAlias);
export const ResolvedVariableType = z.enum(["BOOLEAN", "FLOAT", "STRING", "COLOR"]);

export const Variable = z.object({
  id: z.string(),
  name: z.string(),
  key: z.string(),
  variableCollectionId: z.string(),
  resolvedType: ResolvedVariableType,
  valuesByMode: z.record(VariableValue),
  remote: z.boolean(),
  description: z.string(),
  hiddenFromPublishing: z.boolean(),
  scopes: z.array(VariableScope),
  codeSyntax: z.record(z.any()).optional(),
});

export const VariableMode = z.object({
  modeId: z.string(),
  name: z.string(),
});

export const Collection = z.object({
  id: z.string(),
  name: z.string(),
  modes: z.array(VariableMode),
  defaultModeId: z.string(),
  remote: z.boolean(),
  hiddenFromPublishing: z.boolean(),
});

export const VariablesMapping = z.object({
  variableCollections: z.array(z.string()).min(1),
  variableMode: z.string().min(1),
  supernovaBrand: z.string().min(1),
  supernovaTheme: z.string().min(1).optional().or(z.null()),
});

export const FormattedCollections = z.object({
  variables: z.record(z.string(), Variable),
  variableCollections: z.record(z.string(), Collection),
  mappings: z.array(VariablesMapping).optional(),
  variablesOrder: z.string().array().optional(),
});

//
// Types
//

export type RGB = z.infer<typeof RGB>;
export type RGBA = z.infer<typeof RGBA>;
export type ResolvedVariableType = z.infer<typeof ResolvedVariableType>;
export type Variable = z.infer<typeof Variable>;
export type VariableAlias = z.infer<typeof VariableAlias>;
export type VariableValue = z.infer<typeof VariableValue>;
export type VariableMode = z.infer<typeof VariableMode>;
export type Collection = z.infer<typeof Collection>;
export type VariablesMapping = z.infer<typeof VariablesMapping>;
export type FormattedCollections = z.infer<typeof FormattedCollections>;
